import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import { LinkTo } from '@ember/routing';
import Chip from 'tio-ui/components/chip';
import Text from 'tio-ui/components/text';
import { PhBank, PhStar, PhArrowRight, PhTrendDown, PhCalendar } from 'ember-phosphor-icons';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';

/**
 * LoanProgressCard - Visual loan account card with progress visualization
 *
 * Shows loan balance, progress toward payoff, and key metrics in a
 * visually engaging card format consistent with Career Pathways design.
 *
 * @example
 * ```gts
 * <LoanProgressCard @loan={{this.mohela}} @onSelect={{this.selectLoan}} />
 * ```
 */
interface LoanProgressCardSignature {
  Args: {
    /** The loan account to display */
    loan: LoanAccount;
    /** Whether this is the primary/featured loan */
    featured?: boolean;
    /** Route name for the detail link (defaults to reimagined route) */
    detailRoute?: string;
  };
  Element: HTMLDivElement;
}

export default class LoanProgressCard extends Component<LoanProgressCardSignature> {
  get loan() {
    return this.args.loan;
  }

  get detailRoute() {
    return this.args.detailRoute || 'authenticated.reimagined.loans.detail';
  }

  get progressPercent() {
    const paid = this.loan.originalBalance - this.loan.balance;
    return Math.round((paid / this.loan.originalBalance) * 100);
  }

  get ringProgress() {
    // SVG circle dasharray calculation for progress ring
    const circumference = 2 * Math.PI * 40; // radius = 40
    const offset = circumference - (this.progressPercent / 100) * circumference;
    return htmlSafe(`stroke-dasharray: ${circumference}; stroke-dashoffset: ${offset}`);
  }

  get formattedBalance() {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(this.loan.balance);
  }

  get formattedMonthly() {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(this.loan.monthlyPayment);
  }

  get formattedPayoff() {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric',
    }).format(new Date(this.loan.estimatedPayoff));
  }

  get accountTypeLabel() {
    const labels = {
      federal: 'Federal',
      private: 'Private',
      refinanced: 'Refinanced',
    };
    return labels[this.loan.accountType];
  }

  get accountTypeIntent() {
    const intents = {
      federal: 'primary' as const,
      private: 'warning' as const,
      refinanced: 'success' as const,
    };
    return intents[this.loan.accountType];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="tio-bg-surface rounded-xl p-5 shadow-sm border-2 transition-all hover:shadow-md
        {{if @featured 'tio-loan-progress-card--featured' 'tio-loan-progress-card--standard'}}"
      ...attributes
    >
      {{! Header with servicer and status }}
      <div class="tio-row-start-between mb-4">
        <div class="tio-row">
          <div
            class="p-2.5 rounded-lg
              {{if
                @featured
                'tio-bg-success-soft tio-fg-success'
                'tio-bg-sunken tio-fg-secondary'
              }}"
          >
            <PhBank @size={{24}} />
          </div>
          <div>
            <div class="font-semibold tio-fg-primary">{{this.loan.servicer}}</div>
            <Text @variant="muted" @size="sm">
              {{this.loan.loanCount}}
              {{if (isEqual this.loan.loanCount 1) "loan" "loans"}}
            </Text>
          </div>
        </div>
        <div class="tio-row-tight">
          <Chip @intent={{this.accountTypeIntent}}>{{this.accountTypeLabel}}</Chip>
          {{#if this.loan.isReceivingEmployerHelp}}
            <div
              class="tio-row-extra-tight px-2 py-1 tio-bg-success-soft tio-fg-success-body rounded-full tio-text-xs-medium"
              title="Receiving employer contributions"
            >
              <PhStar @size={{14}} weight="fill" />
              <span>Employer Help</span>
            </div>
          {{/if}}
        </div>
      </div>

      {{! Progress Ring and Balance }}
      <div class="tio-row-extra-loose mb-4">
        {{! Progress Ring }}
        <div class="relative w-24 h-24 shrink-0">
          <svg class="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            {{! Background circle }}
            <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" stroke-width="8" />
            {{! Progress circle }}
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke={{if @featured "#10b981" "#6366f1"}}
              stroke-width="8"
              stroke-linecap="round"
              style={{this.ringProgress}}
            />
          </svg>
          <div class="absolute inset-0 tio-place-center">
            <span class="tio-text-lg-bold tio-fg-primary">{{this.progressPercent}}%</span>
            <span class="text-xs tio-fg-muted">paid</span>
          </div>
        </div>

        {{! Balance and Details }}
        <div class="flex-1">
          <div class="tio-text-3xl-bold tio-fg-primary mb-1">{{this.formattedBalance}}</div>
          <div class="tio-row-extra-tight tio-fg-success text-sm mb-2">
            <PhTrendDown @size={{16}} />
            <span>{{this.formattedMonthly}}/mo payment</span>
          </div>
          <div class="text-sm tio-fg-muted">
            <span class="font-medium">{{this.loan.interestRate}}%</span>
            interest rate
          </div>
        </div>
      </div>

      {{! Footer with payoff info }}
      <div class="tio-row-between pt-4 border-t tio-border-subtle">
        <div class="tio-row-tight tio-fg-secondary">
          <PhCalendar @size={{18}} />
          <span class="text-sm">
            <span class="font-medium">Payoff:</span>
            {{this.formattedPayoff}}
          </span>
        </div>
        <LinkTo
          @route={{this.detailRoute}}
          @model={{this.loan.id}}
          class="tio-row-extra-tight tio-text-sm-medium tio-fg-info hover:tio-fg-info-body"
        >
          <span>View Details</span>
          <PhArrowRight @size={{16}} />
        </LinkTo>
      </div>
    </div>
  </template>
}

const isEqual = (a: unknown, b: unknown) => a === b;
